import { useState, type FormEvent } from 'react';
import Spinner from '../../components/Spinner';
import {
  fosApi, fosError, formatDate, INTERACTION_LABEL, isVersionConflict, newRequestId, scopeFromKey, type InteractionType, type InteractionView, type RelationshipView,
} from '../../lib/fosApi';
import { EmptyState, ErrorNote, Icon } from './ui';

const ICON: Record<InteractionType, string> = { note: 'note', call: 'phone', meeting: 'users', email: 'mail', visit: 'pin', event: 'heart', follow_up: 'clock' };
const today = () => new Date().toISOString().slice(0, 10);

function Composer({ donorId, relationship, edit, onDone, onCancel }: { donorId: string; relationship: RelationshipView; edit?: InteractionView; onDone: () => void; onCancel?: () => void }) {
  const [type, setType] = useState<InteractionType>(edit?.type ?? 'note');
  const [occurredOn, setOccurredOn] = useState(edit?.occurredOn ?? today());
  const [summary, setSummary] = useState(edit?.summary ?? '');
  const [nextStepOn, setNextStepOn] = useState(edit?.nextStepOn ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      if (edit) await fosApi.interaction({ requestId: newRequestId(), operation: 'edit', interactionId: edit.id, expectedVersion: edit.version, type, occurredOn, summary, nextStepOn: nextStepOn || null });
      else await fosApi.interaction({ requestId: newRequestId(), operation: 'add', donorId, scope: scopeFromKey(relationship.scopeKey), type, occurredOn, summary, ...(nextStepOn ? { nextStepOn } : {}) });
      setSummary(''); setNextStepOn(''); onDone();
    } catch (cause) {
      const c = fosError(cause);
      setError(isVersionConflict(c) ? 'This entry changed. Reload the page to see the latest.' : c.message);
    } finally { setBusy(false); }
  };
  return (
    <form className="fos-composer" onSubmit={submit}>
      <div className="fos-composer-row">
        <select value={type} onChange={(e) => setType(e.target.value as InteractionType)} aria-label="Type">
          {(Object.keys(INTERACTION_LABEL) as InteractionType[]).map((t) => <option key={t} value={t}>{INTERACTION_LABEL[t]}</option>)}
        </select>
        <input type="date" value={occurredOn} max={today()} onChange={(e) => setOccurredOn(e.target.value)} aria-label="Date" required />
        <label className="fos-inline">Next step <input type="date" value={nextStepOn} onChange={(e) => setNextStepOn(e.target.value)} aria-label="Next step date" /></label>
      </div>
      <textarea value={summary} onChange={(e) => setSummary(e.target.value)} maxLength={4000} required placeholder="What happened, and what did you learn?" aria-label="What happened" />
      <ErrorNote message={error} />
      <div className="fos-composer-actions">
        {onCancel && <button type="button" className="btn small" onClick={onCancel}>Cancel</button>}
        <button className="btn primary small" disabled={busy || !summary.trim()}>{busy ? <Spinner /> : edit ? 'Save' : 'Add to timeline'}</button>
      </div>
    </form>
  );
}

function Item({ item, donorId, relationship, onChanged }: { item: InteractionView; donorId: string; relationship: RelationshipView; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [retracting, setRetracting] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const canChange = item.status === 'active' && (item.mine || relationship.permissions.manageAssignments);
  const retract = async () => {
    setError('');
    try { await fosApi.interaction({ requestId: newRequestId(), operation: 'retract', interactionId: item.id, expectedVersion: item.version, reason }); onChanged(); }
    catch (cause) { setError(fosError(cause).message); }
  };
  return (
    <li className={`fos-tl-item${item.status === 'retracted' ? ' retracted' : ''}`}>
      <span className="fos-tl-dot"><Icon name={ICON[item.type]} size={14} /></span>
      <div className="fos-tl-body">
        <div className="fos-tl-meta"><strong>{INTERACTION_LABEL[item.type]}</strong><span>{formatDate(item.occurredOn)}</span><span>· {item.authorName}</span></div>
        {editing ? <Composer donorId={donorId} relationship={relationship} edit={item} onDone={() => { setEditing(false); onChanged(); }} onCancel={() => setEditing(false)} />
          : item.status === 'retracted' ? <p className="fos-tl-text muted">This entry was retracted.</p>
            : <p className="fos-tl-text">{item.summary}</p>}
        {item.nextStepOn && item.status === 'active' && !editing && <div className="fos-chip next"><Icon name="clock" size={12} />Next step {formatDate(item.nextStepOn)}</div>}
        {canChange && !editing && (
          <div className="fos-tl-actions">
            <button className="linkbtn" onClick={() => setEditing(true)}>Edit</button>
            {!retracting ? <button className="linkbtn" onClick={() => setRetracting(true)}>Retract</button> : (
              <span className="fos-inline">
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" aria-label="Reason" />
                <button className="btn small danger" onClick={retract} disabled={!reason.trim()}>Retract</button>
                <button className="linkbtn" onClick={() => setRetracting(false)}>Cancel</button>
              </span>
            )}
          </div>
        )}
        <ErrorNote message={error} />
      </div>
    </li>
  );
}

export default function Interactions({ donorId, relationship, onChanged }: { donorId: string; relationship: RelationshipView; onChanged: () => void }) {
  const items = relationship.interactions ?? [];
  const canAdd = relationship.permissions.manageInteractions && relationship.status === 'active';
  return (
    <div className="fos-timeline">
      {canAdd && <Composer donorId={donorId} relationship={relationship} onDone={onChanged} />}
      {relationship.permissions.manageInteractions && relationship.status !== 'active' && <p className="fos-hint">This relationship is inactive. Reactivate it to record activity.</p>}
      {items.length === 0
        ? <EmptyState icon="note" title="No activity yet">{canAdd ? 'Log a call, meeting or note above.' : 'Recorded calls, meetings and notes will appear here.'}</EmptyState>
        : <ul className="fos-tl">{items.map((i) => <Item key={i.id} item={i} donorId={donorId} relationship={relationship} onChanged={onChanged} />)}</ul>}
    </div>
  );
}
